import { HELP_CENTER_ARTICLES } from './customer-support.const';

export interface IRFDC {
  dc2: string;
  dc33: string;
  dc53: string;
  dc63: string;
}

/** Road Facing Dashcam */
export const RFDC: IRFDC = Object.freeze({
  dc2: 'dc2', // 1st generation
  dc33: 'dc33', // 2nd generation
  dc53: 'dc53', // 3rd generation (Hubble)
  dc63: 'dc63', // vg5 single facing
});

export interface IDFDC {
  dc34: string;
  dc54: string;
  dc64: string;
}

/** Dual Facing Dashcam */
export const DFDC: IDFDC = Object.freeze({
  dc34: 'dc34', // 2nd generation
  dc54: 'dc54', // 3rd generation (Hubble)
  dc64: 'dc64', // vg5 dual facing
});

/** Side and Rear Cameras */
export interface ISRC {
  SC1: string,
}

export const SRC: ISRC = Object.freeze({
  SC1: 'SC-1', // side camera model name.
});

export interface ICameraTypes extends IRFDC, IDFDC, ISRC { }

const CAMERA_TYPES: ICameraTypes = Object.freeze({
  ...RFDC,
  ...DFDC,
  ...SRC,
});

export type CameraType = keyof typeof CAMERA_TYPES;

export const AI_DASHCAM_PLUS_CAM_TYPES: string[] = [RFDC.dc63, DFDC.dc64];

export function isSmartCamera(camType: CameraType): boolean {
  return camType === RFDC.dc2 || camType === RFDC.dc33 || camType === DFDC.dc34;
}

export function isHubbleCamera(camType: CameraType): boolean {
  return camType === RFDC.dc53 || camType === DFDC.dc54;
}

export function isRoadFacingCamera(camType: CameraType): boolean {
  return !!RFDC[camType];
}

export function isDualFacingCamera(camType: CameraType): boolean {
  return !!DFDC[camType];
}

export function isRoadFacingAICamera(camType: CameraType): boolean {
  return camType === RFDC.dc53 || camType === RFDC.dc63;
}

export function isDualFacingAICamera(camType: CameraType): boolean {
  return camType === DFDC.dc54 || camType === DFDC.dc64;
}

export function isAIOmnicam(camType: CameraType): boolean {
  return camType === SRC.SC1;
}

export function isAIDashcamPlus(camType: CameraType): boolean {
  return AI_DASHCAM_PLUS_CAM_TYPES.includes(camType);
}

export function isAICamera(camType: CameraType): boolean {
  return isRoadFacingAICamera(camType) || isDualFacingAICamera(camType) || isAIOmnicam(camType);
}

export function getCameraTroubleshootingTipsLink(camType: CameraType): string {
  switch (true) {
  case isAIOmnicam(camType):
    return HELP_CENTER_ARTICLES.AI_OMNICAM_POWER_ISSUE;
  case isSmartCamera(camType):
    return HELP_CENTER_ARTICLES.SMART_DASHCAM_POWER_ISSUE;
  default:
    return HELP_CENTER_ARTICLES.AI_DASHCAM_TROUBLESHOOTING_GUIDE;
  }
}

export enum CameraPosition {
  Road = 'front_facing',
  Driver = 'driver_facing',
  Left = 'side_left',
  Right = 'side_right',
  Rear = 'rear',
  LicensePlate = 'front_narrow_facing',
}

export const DASHCAM_CAMERA_POSITIONS: CameraPosition[] = [
  CameraPosition.Road, CameraPosition.Driver, CameraPosition.LicensePlate,
];

export enum VG5CameraStorageMode {
  HighVideoQuality = 0,
  HighestVideoQuality = 1,
  HighestVideoStorage = 2,
}

export enum CameraStorageMode {
  HighestVideoStorage = 5,
  HighVideoStorage = 4,
  HighestVideoQuality = 3,
  HighVideoQuality = 1,
}

export const VG5_CAMERA_STORAGE_MODE_LABELS: Record<VG5CameraStorageMode, string> = {
  [VG5CameraStorageMode.HighVideoQuality]: $localize`Optimized quality and footage (recommended)`,
  [VG5CameraStorageMode.HighestVideoQuality]: $localize`Highest video quality`,
  [VG5CameraStorageMode.HighestVideoStorage]: $localize`Most available footage`,
};

export const CAMERA_STORAGE_MODE_LABELS: Record<CameraStorageMode, string> = {
  [CameraStorageMode.HighestVideoStorage]: $localize`Highest video storage`,
  [CameraStorageMode.HighVideoStorage]: $localize`High video storage`,
  [CameraStorageMode.HighVideoQuality]: $localize`High video quality (default)`,
  [CameraStorageMode.HighestVideoQuality]: $localize`Highest video quality`,
};
